import { stockProblem } from '../data/yotyAvailability';
import ShippingOptions, { DELIVERY_FEE } from '../components/ShippingOptions';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../components/CartContext';
import { MessageSquare } from 'lucide-react';
import { S3_IMAGES } from '../config/s3Images';

export default function CheckoutPage() {
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart, shippingMethod, setShippingMethod } = useCart();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    telegram: '',
    confirmEmail: '',
    orderNotes: '',
    recipient: '', street: '', unit: '', postal: ''
  });

  const [agreements, setAgreements] = useState({
    pdpa: false,
    photography: false,
  });

  const [justSubmitted, setJustSubmitted] = useState(false);

  // Redirect if cart is empty
  useEffect(() => {
  if (!justSubmitted && cartItems.length === 0) {
    navigate('/shop');
  }
}, [cartItems, justSubmitted, navigate]);

  const hasTickets = cartItems.some(item => item.type === 'ticket');
  const hasMerch = cartItems.some(item => item.type !== 'ticket');
  const isDelivery = hasMerch && shippingMethod === 'delivery';
  const subtotal = getCartTotal();
  const shippingCost = isDelivery ? DELIVERY_FEE : 0;
  const total = subtotal + shippingCost;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAgreementChange = (e) => {
    setAgreements({
      ...agreements,
      [e.target.name]: e.target.checked
    });
  };

  const handleSubmit = async () => {
    const stockError = stockProblem(undefined, cartItems);
    if (stockError) { alert(stockError); return; }
  if (submittingRef.current) return;
  // Validate required fields
  if (!formData.firstName || !formData.lastName || !formData.email ||
      !formData.phone || !formData.telegram || !formData.confirmEmail) {
    alert("Please fill in all required fields");
    return;
  }

  if (formData.email !== formData.confirmEmail) {
    alert("Email addresses do not match");
    return;
  }

  if (!agreements.pdpa) {
    alert("Please accept the PDPA consent.");
    return;
  }

  if (hasTickets && !agreements.photography) {
    alert("Please accept the photography consent.");
    return;
  }

  if (!cartItems || cartItems.length === 0) {
    alert("Cart is empty");
    return;
  }

  if (isDelivery) {
    const invalidField = !formData.recipient.trim() ? ['recipient', 'Please enter the delivery recipient name.']
      : !formData.street.trim() ? ['street', 'Please enter the delivery street address.']
      : !/^\d{6}$/.test(formData.postal.trim()) ? ['postal', 'Please enter a six-digit Singapore postal code.']
      : !/^\+?[\d\s-]{8,15}$/.test(formData.phone.trim()) ? ['phone', 'Please enter a valid contact number, for example 9876 3468 or +65 9876 3468.'] : null;
    if (invalidField) {
      alert(invalidField[1]);
      document.querySelector(`[name="${invalidField[0]}"]`)?.focus();
      return;
    }
  }
  submittingRef.current = true;
  setSubmitting(true);
  try {
    const base = process.env.REACT_APP_API_BASE;

    // convert frontend cart items into backend-friendly items
    // supports:
    // - normal products: { id, name, price, quantity, size, image }
    // - event tickets:  { id, name, price, quantity, image, type:'ticket', eventDate }
    const normalizedItems = cartItems.map((item) => ({
      // "product_id" for merch, "event_id" for tickets
      product_id: item.type === "ticket" ? null : (item.product_id ?? item.id),
      event_id: item.type === "ticket" ? (item.event_id ?? item.id) : null,
      item_type: item.type === "ticket" ? "event" : "product",

      name: item.name,
      size: item.size ?? null,
      selections: item.selections || [],
      image: item.image ?? null,
      quantity: Number(item.quantity ?? 1),
      price: Number(item.price ?? 0),
    }));

    const payload = {
      cartItems: normalizedItems,

      shippingMethod, // 'self-collection' | 'delivery' | 'tracked-mail'
      paymentMethod: "PayNow",

      customer: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        telegram: formData.telegram,
      },

      orderNotes: formData.orderNotes || "",

      // only needed if delivery — for now you can keep simple
      shippingAddress:
        shippingMethod === "self-collection"
          ? null
          : {
              name: formData.recipient.trim(),
              line1: formData.street.trim(),
              line2: formData.unit.trim(),
              postalCode: formData.postal.trim(),
              country: "SG",
            },
    };

    const res = await fetch(`${base}/api/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (!res.ok || !data.success) {
      console.error("Checkout failed:", data);
      alert(data.error || "Checkout failed");
      return;
    }

    // Clear cart locally after successful order
    setJustSubmitted(true);
    clearCart();

    // Navigate to URL-based confirmation page so users can revisit via link
    navigate(`/order-confirmation/${data.orderId}`, {
      state: { orderId: data.orderId, orderNumber: data.orderNumber },
    });

  } catch (err) {
    console.error(err);
    alert("Network/server error. Please try again.");
  } finally {
    submittingRef.current = false;
    setSubmitting(false);
  }
  };


    // // Log order for backend team (this is where you'll send to backend)
    // console.log('Order submitted:', orderData);
    
    // // Navigate to order confirmation page with order data
    // navigate('/order-confirmation', { 
    //   state: { orderData }
    // });

    // Optional: Clear cart after successful order
    // Uncomment the line below if you want to clear the cart
    // clearCart();
  // };

  if (cartItems.length === 0) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">
            For any additional notes/queries about your order, please email AFTH-SALES@e.ntu.edu.sg
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Billing Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-teal-500 mb-6">Review & Checkout</h2>
              
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">
                    Last name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-sm text-gray-700 mb-2">
                  Email address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Phone */}
              <div className="mb-4">
                <label className="block text-sm text-gray-700 mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Telegram Handle */}
              <div className="mb-4">
                <label className="block text-sm text-gray-700 mb-2">
                  Telegram Handle <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="telegram"
                  value={formData.telegram}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Ship to different address */}
              {/* <div className="mb-4">
                <label className="flex items-center gap-2 text-teal-500 font-medium cursor-pointer">
                  <input type="checkbox" className="w-4 h-4" />
                  Ship to a different address?
                </label>
              </div> */}

              {/* Confirm Email */}
              <div className="mb-4">
                <label className="block text-sm text-gray-700 mb-2">
                  Confirm email address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="confirmEmail"
                  value={formData.confirmEmail}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {isDelivery && <fieldset className="mb-6 space-y-4">
                <legend className="text-xl font-semibold mb-3">Delivery address</legend>
                {[['recipient', 'Recipient name'], ['street', 'Street address'], ['unit', 'Unit number (optional)'], ['postal', 'Singapore postal code']].map(([name, label]) => <label key={name} className="block text-sm text-gray-700">{label}
                  <input name={name} value={formData[name]} onChange={handleInputChange} required={name !== 'unit'} autoComplete={{recipient:'shipping name',street:'shipping address-line1',unit:'shipping address-line2',postal:'shipping postal-code'}[name]} inputMode={name === 'postal' ? 'numeric' : 'text'} maxLength={name === 'postal' ? 6 : 200} className="mt-2 w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </label>)}
                <p className="text-sm text-gray-600">Use the contact number above for delivery. Tracked mailing costs $5.00, with fulfilment expected in January 2027.</p>
              </fieldset>}
              {/* Order Notes */}
              <div className="mb-6">
                <label className="block text-sm text-gray-700 mb-2">
                  Order notes (optional)
                </label>
                <textarea
                  name="orderNotes"
                  value={formData.orderNotes}
                  onChange={handleInputChange}
                  placeholder="Notes about your order, e.g. special notes for collection."
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Agreements */}
              <div className="space-y-4">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="pdpa"
                    checked={agreements.pdpa}
                    onChange={handleAgreementChange}
                    className="w-4 h-4 mt-1 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700">
                    I consent to NTU CAC Arts From The Heart collecting and using my personal particulars solely for order/payment-related updates and to facilitate delivery or collection of my order. <span className="text-red-500">*</span>
                  </span>
                </label>

                {hasTickets && <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="photography"
                    checked={agreements.photography}
                    onChange={handleAgreementChange}
                    className="w-4 h-4 mt-1 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700">
                    Photography Consent: By submitting this form, I hereby grant permission to NTU CAC Arts From The Heart to take photographs of me during the event for documentation, publicity, and communication purposes. <span className="text-red-500">*</span>
                  </span>
                </label>}
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h2 className="text-2xl font-bold text-teal-500 mb-6">Your order</h2>
              
              {/* Product List */}
              <div className="border-b border-gray-200 pb-4 mb-4">
                <div className="flex justify-between text-sm font-medium text-gray-600 mb-3">
                  <span>PRODUCT</span>
                  <span>SUBTOTAL</span>
                </div>
                
                {cartItems.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm text-gray-700 mb-2">
                    <span className="flex-1">{item.name} {item.size && `(${item.size})`} × {item.quantity}</span>
                    <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Subtotal */}
              <div className="flex justify-between text-sm mb-3">
                <span className="text-gray-700">Subtotal</span>
                <span className="font-medium text-gray-900">${subtotal.toFixed(2)}</span>
              </div>

              <div className="border-b border-gray-200 pb-4 mb-4">
                <ShippingOptions hasMerch={hasMerch} shippingMethod={shippingMethod} onChange={setShippingMethod} />
              </div>

              {/* Total */}
              <div className="flex justify-between text-base font-bold mb-6">
                <span className="text-gray-900">Total</span>
                <span className="text-gray-900">${total.toFixed(2)}</span>
              </div>

              {/* Payment Info */}
              <div className="bg-gray-50 rounded p-4 mb-4">
                <div className="font-medium text-gray-900 mb-2">PayNow</div>
                <p className="text-xs text-gray-600 leading-relaxed mb-2">
                  Payment via PayNow QR Code.
                </p>
                <ul className="list-decimal list-inside text-xs text-gray-600 space-y-1 mb-3">
                  <li>Please upload proof of transaction.</li>
                  <li>
                    Your order will only be confirmed once payment has been verified. Once verified,
                    you will receive a payment verification email in your inbox.
                  </li>
                </ul>
                <div className="flex justify-center">
                  <img
                    src={S3_IMAGES.PAYNOW_QR}
                    alt="PayNow QR Code"
                    className="w-40 h-40 object-contain rounded"
                  />
                </div>
              </div>

              {/* Place Order Button */}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-teal-400 hover:bg-teal-500 text-white font-medium py-3 rounded transition-colors"
              >
                {submitting ? 'SUBMITTING...' : 'PLACE ORDER'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}